// AdminProfileDrawer.jsx
import React, { useState, useEffect } from "react";
import { adminApi, clearAdminToken } from "./adminApi";
import "./EditUserDrawer.css"; // reuses the same drawer/field styling as EditUserDrawer

function generateRandomPassword() {
  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let result = "";
  for (let i = 0; i < 10; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
}

function initials(name) {
  return (name || "?").trim().charAt(0).toUpperCase();
}

const UserIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
  </svg>
);
const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const LogoutIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </svg>
);

export default function AdminProfileDrawer({ onClose, onLogout }) {
  const [username, setUsername] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameSaved, setUsernameSaved] = useState(false);
  const [usernameCopied, setUsernameCopied] = useState(false);

  const [currentPassword, setCurrentPassword] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(true);
  const [passwordError, setPasswordError] = useState("");
  const [passwordCopied, setPasswordCopied] = useState(false);

  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [resetSaving, setResetSaving] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetSaved, setResetSaved] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    setPasswordLoading(true);
    setPasswordError("");
    try {
      const profile = await adminApi.getMyProfile();
      setUsername(profile.username);
      setUsernameInput(profile.username);
      setCurrentPassword(profile.password);
      if (!profile.password) {
        setPasswordError(
          "No viewable password stored yet for this account. Set one below with a manual entry or generated one."
        );
      }
    } catch (err) {
      setPasswordError(err.message || "Could not load profile.");
    } finally {
      setPasswordLoading(false);
    }
  }

  async function handleSaveUsername() {
    if (usernameInput.trim() === username) return;
    setUsernameError("");
    setUsernameSaving(true);
    try {
      await adminApi.updateMyUsername(usernameInput.trim());
      setUsername(usernameInput.trim());
      setUsernameSaved(true);
      setTimeout(() => setUsernameSaved(false), 2000);
    } catch (err) {
      setUsernameError(err.message || "Could not update username.");
    } finally {
      setUsernameSaving(false);
    }
  }

  function handleGenerateRandom() {
    setNewPasswordInput(generateRandomPassword());
    setResetError("");
  }

  async function handleSaveNewPassword() {
    if (!newPasswordInput.trim()) return;
    setResetError("");
    setResetSaving(true);
    try {
      const result = await adminApi.resetMyPassword(newPasswordInput.trim());
      setCurrentPassword(result.plaintextPassword);
      setNewPasswordInput("");
      setPasswordError("");
      setResetSaved(true);
      setTimeout(() => setResetSaved(false), 2000);
    } catch (err) {
      setResetError(err.message || "Could not save that password.");
    } finally {
      setResetSaving(false);
    }
  }

  function copyUsername() {
    navigator.clipboard.writeText(username);
    setUsernameCopied(true);
    setTimeout(() => setUsernameCopied(false), 2000);
  }

  function copyPassword() {
    if (!currentPassword) return;
    navigator.clipboard.writeText(currentPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  }

  function handleLogoutClick() {
    clearAdminToken();
    onLogout();
  }

  return (
    <div className="eud-overlay" onClick={onClose}>
      <div className="eud-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="eud-header">
          <div className="eud-header-left">
            <div className="eud-avatar">{initials(username)}</div>
            <div>
              <h2 className="eud-title">Your Profile</h2>
              <p className="eud-subtitle">Manage your admin login</p>
            </div>
          </div>
          <button className="eud-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="eud-body">
          {/* Username */}
          <div className="eud-field-group">
            <div className="eud-label-row">
              <span className="eud-label-icon"><UserIcon /></span>
              <span className="eud-label">Username</span>
            </div>
            <div className="eud-inline-row">
              <input
                className="eud-input"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
              />
              <button className="eud-btn-icon" onClick={copyUsername} title="Copy username">
                {usernameCopied ? "✓" : "Copy"}
              </button>
              <button
                className="eud-btn-small"
                onClick={handleSaveUsername}
                disabled={usernameSaving || usernameInput.trim() === username}
              >
                {usernameSaving ? "Saving…" : usernameSaved ? "Saved ✓" : "Save"}
              </button>
            </div>
            {usernameError && <div className="eud-error">{usernameError}</div>}
          </div>

          {/* Current password */}
          <div className="eud-field-group">
            <div className="eud-label-row">
              <span className="eud-label-icon"><LockIcon /></span>
              <span className="eud-label">Current Password</span>
            </div>

            {passwordLoading ? (
              <div className="eud-hint">Loading…</div>
            ) : currentPassword ? (
              <div className="eud-inline-row">
                <input className="eud-input eud-mono" value={currentPassword} readOnly />
                <button className="eud-btn-icon" onClick={copyPassword} title="Copy password">
                  {passwordCopied ? "✓" : "Copy"}
                </button>
              </div>
            ) : (
              <div className="eud-warning">{passwordError}</div>
            )}
          </div>

          {/* Set new password */}
          <div className="eud-field-group">
            <div className="eud-label-row">
              <span className="eud-label-icon"><LockIcon /></span>
              <span className="eud-label">Set New Password</span>
            </div>

            <div className="eud-inline-row">
              <input
                className="eud-input eud-mono"
                type="text"
                placeholder="Type a new password, or generate one…"
                value={newPasswordInput}
                onChange={(e) => setNewPasswordInput(e.target.value)}
              />
              <button className="eud-btn-icon" onClick={handleGenerateRandom} title="Fill with a random password">
                Generate
              </button>
            </div>
            <button
              className="eud-btn-small eud-btn-full"
              onClick={handleSaveNewPassword}
              disabled={resetSaving || !newPasswordInput.trim()}
              style={{ marginTop: 8 }}
            >
              {resetSaving ? "Saving…" : resetSaved ? "Saved ✓" : "Save Password"}
            </button>
            <p className="eud-hint">
              Nothing changes until you click "Save Password" - generating just fills in a
              suggestion you can edit first.
            </p>
            {resetError && <div className="eud-error">{resetError}</div>}
          </div>

          {/* Sign out */}
          <div className="eud-field-group">
            <div className="eud-label-row">
              <span className="eud-label-icon"><LogoutIcon /></span>
              <span className="eud-label">Session</span>
            </div>
            <button className="eud-btn-danger-ghost" onClick={handleLogoutClick}>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}